/**
 * Public seller vehicle intake form (Milestones 3–7).
 * Validation, API submission, and production-ready result states.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { submitSellerVehicleIntake } from "../../../services/sellerSubmissionService";
import { uploadSellerSubmissionImages } from "../../../services/sellerSubmissionImageService";
import {
  createEmptySellerSubmissionInput,
  createSellerSubmissionToken,
  focusFirstSellerSubmissionError,
  mapSellerSubmissionApiErrorsToFormErrors,
  validateSellerSubmissionInput,
} from "../../../utils/sellerIntake";
import { revokeSellerPhotoPreviews } from "../../../utils/sellerIntake/sellerPhotoValidation";
import {
  getElapsedTimeBucket,
  resolveLastCompletedFormStep,
  trackSellerIntakeFieldChanged,
  trackSellerIntakeFormAbandoned,
  trackSellerIntakeFormStarted,
  trackSellerIntakeResetClicked,
  trackSellerIntakeSubmissionResult,
  trackSellerIntakeSubmitAttempted,
  trackSellerIntakeValidationError,
} from "../../../utils/sellerIntake/sellerIntakeAnalytics";
import SellerConditionFields from "./SellerConditionFields";
import SellerConsentFields from "./SellerConsentFields";
import SellerContactFields from "./SellerContactFields";
import SellerPricingFields from "./SellerPricingFields";
import SellerSubmissionResultPanel from "./SellerSubmissionResultPanel";
import SellerVehicleFields from "./SellerVehicleFields";
import { SELLER_SUBMIT_UI_STATES } from "./sellerSubmissionResultConstants";
import {
  mapSellerSubmissionResultToSubmitState,
  resolveSellerSubmissionSubmitPresentation,
} from "./sellerSubmissionSubmitPresentation";
import { useLocale } from "../../../intl";
import { useSellerIntakeCopy } from "../../../intl/productFormCopy.js";

/**
 * @param {{ disabled?: boolean, className?: string }} [props]
 */
export default function SellerIntakeForm({ disabled = false, className = "" }) {
  const [input, setInput] = useState(createEmptySellerSubmissionInput);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitState, setSubmitState] = useState(SELLER_SUBMIT_UI_STATES.IDLE);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoUploadState, setPhotoUploadState] = useState(null);
  const resultHeadingRef = useRef(null);
  const inlineSubmitRef = useRef(null);
  const [showStickySubmit, setShowStickySubmit] = useState(false);
  const submissionTokenRef = useRef(createSellerSubmissionToken());
  const submitInFlightRef = useRef(false);
  const formStartedRef = useRef(false);
  const formSubmittedRef = useRef(false);
  const abandonmentTrackedRef = useRef(false);
  const formSessionStartedAtRef = useRef(null);
  const submitAttemptCountRef = useRef(0);
  const changedFieldsRef = useRef(new Set());
  const trackedFieldChangesRef = useRef(new Set());

  const trackFormAbandoned = useCallback(() => {
    if (abandonmentTrackedRef.current || !formStartedRef.current || formSubmittedRef.current) {
      return;
    }
    abandonmentTrackedRef.current = true;
    trackSellerIntakeFormAbandoned({
      lastCompletedStep: resolveLastCompletedFormStep(changedFieldsRef.current),
      changedFieldCount: changedFieldsRef.current.size,
      elapsedTimeBucket: getElapsedTimeBucket(formSessionStartedAtRef.current),
    });
  }, []);

  const { t } = useLocale();
  const copy = useSellerIntakeCopy();

  const presentation = useMemo(
    () =>
      resolveSellerSubmissionSubmitPresentation({
        submitState,
        submissionResult,
        photoUploadState,
        t,
      }),
    [submitState, submissionResult, photoUploadState, t]
  );

  const isSubmitting = submitState === SELLER_SUBMIT_UI_STATES.SUBMITTING;
  const submitDisabled = disabled || isSubmitting;

  useEffect(() => {
    if (!presentation.showResultPanel) return;
    resultHeadingRef.current?.focus?.();
  }, [presentation.showResultPanel, submitState]);

  useEffect(() => {
    if (!presentation.showForm || !presentation.showSubmit) {
      setShowStickySubmit(false);
      return undefined;
    }

    const target = inlineSubmitRef.current;
    if (!target || typeof IntersectionObserver === "undefined") {
      setShowStickySubmit(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickySubmit(!entry.isIntersecting);
      },
      { root: null, threshold: 0.01 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [presentation.showForm, presentation.showSubmit]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        trackFormAbandoned();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      trackFormAbandoned();
    };
  }, [trackFormAbandoned]);

  const handleChange = useCallback(
    (patch) => {
      setInput((current) => ({ ...current, ...patch }));

      Object.keys(patch).forEach((fieldName) => {
        if (fieldName === "honeypot") return;

        changedFieldsRef.current.add(fieldName);

        if (!trackedFieldChangesRef.current.has(fieldName)) {
          trackedFieldChangesRef.current.add(fieldName);
          trackSellerIntakeFieldChanged(fieldName);
        }

        if (!formStartedRef.current) {
          formStartedRef.current = true;
          formSessionStartedAtRef.current = Date.now();
          trackSellerIntakeFormStarted();
        }
      });

      if (submitState === SELLER_SUBMIT_UI_STATES.SUCCESS) {
        return;
      }

      if (submitState !== SELLER_SUBMIT_UI_STATES.IDLE && submitState !== SELLER_SUBMIT_UI_STATES.EDITING) {
        setSubmitState(SELLER_SUBMIT_UI_STATES.EDITING);
        setSubmissionResult(null);
      }

      if (!submitAttempted) return;

      setFieldErrors((current) => {
        const next = { ...current };
        Object.keys(patch).forEach((key) => {
          delete next[key];
        });
        return next;
      });
    },
    [submitAttempted, submitState]
  );

  const resetForm = useCallback(() => {
    trackSellerIntakeResetClicked();
    revokeSellerPhotoPreviews(selectedPhotos);
    setInput(createEmptySellerSubmissionInput());
    setSelectedPhotos([]);
    setPhotoUploadState(null);
    setFieldErrors({});
    setSubmissionResult(null);
    setSubmitAttempted(false);
    setSubmitState(SELLER_SUBMIT_UI_STATES.IDLE);
    submissionTokenRef.current = createSellerSubmissionToken();
    submitInFlightRef.current = false;
    formStartedRef.current = false;
    formSubmittedRef.current = false;
    abandonmentTrackedRef.current = false;
    formSessionStartedAtRef.current = null;
    submitAttemptCountRef.current = 0;
    changedFieldsRef.current = new Set();
    trackedFieldChangesRef.current = new Set();
    const title = document.getElementById("seller-intake-form-title");
    title?.focus?.();
    title?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [selectedPhotos]);

  const executeSubmit = useCallback(async () => {
    if (submitInFlightRef.current) return;

    setSubmitAttempted(true);
    setSubmissionResult(null);

    const validation = validateSellerSubmissionInput(input);

    if (validation.spam) {
      setFieldErrors({});
      setSubmitState(SELLER_SUBMIT_UI_STATES.SPAM_REJECTED);
      trackSellerIntakeSubmissionResult({
        ok: false,
        status: "spam_rejected",
        retryable: false,
      });
      return;
    }

    if (!validation.valid) {
      setFieldErrors(validation.fieldErrors);
      setSubmitState(SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR);
      trackSellerIntakeValidationError(validation.fieldErrors);
      focusFirstSellerSubmissionError(validation.fieldErrors);
      return;
    }

    setFieldErrors({});
    setSubmitState(SELLER_SUBMIT_UI_STATES.SUBMITTING);
    submitInFlightRef.current = true;
    submitAttemptCountRef.current += 1;

    trackSellerIntakeSubmitAttempted(input, {
      submitAttemptCount: submitAttemptCountRef.current,
      elapsedTimeBucket: getElapsedTimeBucket(formSessionStartedAtRef.current),
    });

    try {
      const apiResult = await submitSellerVehicleIntake(input, {
        submissionToken: submissionTokenRef.current,
      });

      let finalResult = apiResult;

      if (apiResult.ok && selectedPhotos.length > 0 && apiResult.submissionId && apiResult.uploadToken) {
        setPhotoUploadState({ uploading: true, completed: 0, total: selectedPhotos.length });
        const uploadResult = await uploadSellerSubmissionImages(
          apiResult.submissionId,
          selectedPhotos.map((entry) => entry.file),
          {
            uploadToken: apiResult.uploadToken,
            onProgress: (completed, total) => {
              setPhotoUploadState({ uploading: true, completed, total });
            },
          }
        );
        setPhotoUploadState({
          uploading: false,
          completed: uploadResult.successCount,
          total: selectedPhotos.length,
          partial: uploadResult.partial,
          failed: uploadResult.failureCount > 0,
        });
        finalResult = {
          ...apiResult,
          photoUploadStatus: uploadResult.partial
            ? "partial"
            : uploadResult.ok
              ? "success"
              : "failed",
        };
      } else if (apiResult.ok && selectedPhotos.length === 0) {
        finalResult = {
          ...apiResult,
          photoUploadStatus: "skipped",
        };
      }

      setSubmissionResult(finalResult);

      const nextState = mapSellerSubmissionResultToSubmitState(finalResult);
      setSubmitState(nextState);

      if (nextState === SELLER_SUBMIT_UI_STATES.SUCCESS) {
        formSubmittedRef.current = true;
        revokeSellerPhotoPreviews(selectedPhotos);
        setSelectedPhotos([]);
        submissionTokenRef.current = createSellerSubmissionToken();
      }

      trackSellerIntakeSubmissionResult(finalResult, {
        submitAttemptCount: submitAttemptCountRef.current,
        elapsedTimeBucket: getElapsedTimeBucket(formSessionStartedAtRef.current),
      });

      if (nextState === SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR) {
        const apiFieldErrors =
          apiResult.formErrors || mapSellerSubmissionApiErrorsToFormErrors(apiResult.fieldErrors);
        const { form: _formLevelError, ...rest } = apiFieldErrors;
        setFieldErrors(rest);
        focusFirstSellerSubmissionError(rest);
      }
    } catch {
      const networkResult = {
        ok: false,
        status: "network_error",
        submissionId: null,
        sellerMessage: null,
        retryable: true,
        fieldErrors: [],
        errorCategory: "network_error",
      };
      setSubmissionResult(networkResult);
      setSubmitState(SELLER_SUBMIT_UI_STATES.NETWORK_ERROR);
      trackSellerIntakeSubmissionResult(networkResult, {
        submitAttemptCount: submitAttemptCountRef.current,
      });
    } finally {
      submitInFlightRef.current = false;
    }
  }, [input, selectedPhotos]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled || isSubmitting) return;
      void executeSubmit();
    },
    [disabled, executeSubmit, isSubmitting]
  );

  const handleRetry = useCallback(() => {
    setSubmissionResult(null);
    setSubmitState(SELLER_SUBMIT_UI_STATES.EDITING);
    void executeSubmit();
  }, [executeSubmit]);

  const wrapperClassName = [
    "sell-car-form",
    presentation.showForm && presentation.showSubmit && showStickySubmit ? "sell-car-form--with-sticky" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const formId = "seller-intake-form";

  return (
    <div className={wrapperClassName}>
      <SellerSubmissionResultPanel
        presentation={presentation}
        resultHeadingRef={resultHeadingRef}
        onRetry={handleRetry}
        onSubmitAnother={resetForm}
      />

      {presentation.showForm ? (
        <>
          <form id={formId} onSubmit={handleSubmit} noValidate aria-labelledby="seller-intake-form-title">
            <div className="sell-car-form__intro">
              <h2 id="seller-intake-form-title">{copy.sectionTitle}</h2>
              <p>{copy.sectionIntro}</p>
              <p className="sell-car-form__required-legend">
                <span className="sell-car-form__required" aria-hidden="true">
                  *
                </span>{" "}
                {copy.requiredMarker}
              </p>
              <ol className="sell-car-form__section-steps" aria-label="Secciones del formulario">
                {copy.formSectionSteps.map((step, index) => (
                  <li key={step}>
                    <span className="sell-car-form__section-step-index">{index + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>

          <div className="sell-car-form__honeypot" aria-hidden="true">
            <label htmlFor="seller-intake-honeypot">Website</label>
            <input
              id="seller-intake-honeypot"
              tabIndex={-1}
              autoComplete="off"
              value={input.honeypot}
              onChange={(event) => handleChange({ honeypot: event.target.value })}
            />
          </div>

          <SellerContactFields
            input={input}
            onChange={handleChange}
            errors={fieldErrors}
            disabled={submitDisabled}
          />
          <SellerVehicleFields
            input={input}
            onChange={handleChange}
            errors={fieldErrors}
            disabled={submitDisabled}
          />
          <SellerPricingFields
            input={input}
            onChange={handleChange}
            errors={fieldErrors}
            disabled={submitDisabled}
          />
          <SellerConditionFields
            input={input}
            onChange={handleChange}
            selectedPhotos={selectedPhotos}
            onPhotosChange={setSelectedPhotos}
            errors={fieldErrors}
            disabled={submitDisabled}
          />
          <SellerConsentFields
            input={input}
            onChange={handleChange}
            errors={fieldErrors}
            disabled={submitDisabled}
          />

          {presentation.showSubmit ? (
            <div className="sell-car-form__actions">
              {submitAttempted && Object.keys(fieldErrors).length > 0 ? (
                <div className="sell-car-form__form-error" role="alert">
                  <p>Revisa los campos marcados antes de enviar.</p>
                </div>
              ) : null}
              <button
                ref={inlineSubmitRef}
                type="submit"
                className="sell-car-page__btn sell-car-page__btn--primary sell-car-form__submit"
                disabled={submitDisabled}
                aria-busy={isSubmitting}
              >
                {isSubmitting
                  ? copy.submitPendingLabel
                  : copy.submitLabel}
              </button>
            </div>
          ) : null}
          </form>

          {presentation.showSubmit && showStickySubmit ? (
            <div className="sell-car-form__sticky-submit">
              <button
                type="submit"
                form={formId}
                className="sell-car-page__btn sell-car-page__btn--primary sell-car-form__submit"
                disabled={submitDisabled}
                aria-busy={isSubmitting}
              >
                {isSubmitting
                  ? copy.submitPendingLabel
                  : copy.submitLabel}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
